import { ECONOMY } from '@/config/economy';
import type { Rng } from '@/core/rng';
import type { Arena } from '@/arena/arena';
import type { Boss } from '@/bosses/boss-base';
import type { BossEvents, BossPoint, BurstFx, ChainFx } from '@/bosses/boss-hooks';
import type { GemSystem } from '@/entities/gems';
import type { MinionSystem } from '@/entities/minions';
import type { Camera } from '@/render/camera';
import type { FxSystem } from '@/render/fx';
import { sfx } from '@/audio/sfx';

/** Scene systems the boss hooks report into (scene/world.ts provides them). */
export interface BossHookHost {
  readonly minions: MinionSystem;
  readonly gems: GemSystem;
  readonly fx: FxSystem;
  readonly camera: Camera;
  readonly arena: Pick<Arena, 'clearPoint'>;
}

/**
 * Wire a freshly spawned boss to the host — ONE generic path for every boss
 * (no per-class instanceof): phase-change trickle + drones, own-summon
 * minion hooks (Hive waves, reinforcements) and bespoke event feedback.
 * @param rng read per call — the run RNG is replaced on new game / continue
 */
export function wireBossHooks(boss: Boss, host: BossHookHost, rng: () => Rng): void {
  boss.onPhaseEnter = (phase) => {
    if (phase.spawnDrones) {
      host.minions.spawn('drone', phase.spawnDrones, boss.x, boss.y);
    }
    // Mid-fight yellow trickle on every phase change (docs/03)
    host.gems.spawnBurst(boss.x, boss.y, ECONOMY.phaseGemBurst, 1, rng());
  };
  // Shields count OWN summons only
  boss.minionCount = () => host.minions.summonedCount;
  boss.spawnMinions = (kind, count) => {
    host.minions.spawn(kind, count, boss.x, boss.y, true);
  };
  boss.events = bossEvents(host, rng);
}

function bossEvents(host: BossHookHost, rng: () => Rng): BossEvents {
  return {
    burst(x: number, y: number, fx: BurstFx): void {
      if (fx.blast === 'big') host.fx.bigExplosion(x, y);
      else host.fx.explosion(x, y, fx.blast);
      if (fx.shake > 0) host.camera.shake(fx.shake);
      if (fx.sfx > 0) sfx.explosion(fx.sfx);
      if (fx.gems > 0) host.gems.spawnBurst(x, y, fx.gems, 1, rng());
    },
    chain(points: ReadonlyArray<BossPoint>, fx: ChainFx): void {
      points.forEach((p, i) => {
        if (i % 2 === 0) host.fx.explosion(p.x, p.y, fx.blast);
      });
      host.camera.shake(fx.shake);
      sfx.explosion(fx.sfx);
      const at = fx.gemsAt === 'mid'
        ? points[Math.floor(points.length / 2)]!
        : points[points.length - 1]!;
      host.gems.spawnBurst(at.x, at.y, fx.gems, 1, rng());
    },
    clearPoint(nx, ny, avoidX, avoidY, away, radius): BossPoint {
      return host.arena.clearPoint(nx, ny, avoidX, avoidY, away, radius);
    },
  };
}
